import { Button } from '@neo/index'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@neo/primitives/dialog'
import { useUpdateNodeInternals } from '@xyflow/react'
import { v4 } from 'uuid'
import { MenuNodeID } from '../config/nodes.config'
import { useTypedReactFlow } from '../hooks/use-react-flow'
import { useFlowStore } from '../store'
import { FlowNodeTypes, type GenericNode } from '../types'
import { clickNode, getDefaultNodeData } from '../utils'
import { connectChildNode } from '../utils/node-connect'

// The Initiate Call output handle an AI conversation continues from once the call
// is answered. See initiate_call output states in config/nodes.config.tsx.
const INITIATE_CALL_ANSWERED_HANDLE = 'answered'

export const CallTriggerPromptDialog = () => {
  const { getNode, getNodes, addNodes, addEdges, deleteElements } = useTypedReactFlow()
  const updateNodeInternals = useUpdateNodeInternals()
  const callTriggerPrompt = useFlowStore((state) => state.callTriggerPrompt)
  const setCallTriggerPrompt = useFlowStore((state) => state.setCallTriggerPrompt)
  const setCandidateNode = useFlowStore((state) => state.setCandidateNode)

  // Remove the transient drag placeholder (created when dragging from a handle to
  // empty canvas) so it does not linger once we take over the add.
  const clearDragPlaceholder = () => {
    deleteElements({ nodes: [{ id: MenuNodeID }], edges: [{ id: MenuNodeID }] })
  }

  const handleCancel = () => {
    if (callTriggerPrompt?.isDrag) clearDragPlaceholder()
    setCallTriggerPrompt(null)
  }

  const handleAddInitiateCall = () => {
    const prompt = callTriggerPrompt
    if (!prompt) return

    // No parent to chain under (standalone "+ Add node"): drop a single Initiate
    // Call as the mouse-following candidate; the user wires the AI conversation next.
    if (prompt.isStandalone || !prompt.sourceNodeId) {
      setCandidateNode({
        id: v4(),
        type: FlowNodeTypes.initiate_call,
        position: { x: 0, y: 0 },
        data: getDefaultNodeData(FlowNodeTypes.initiate_call, getNodes),
      } as GenericNode)
      setCallTriggerPrompt(null)
      return
    }

    const sourceNode = getNode(prompt.sourceNodeId)
    if (!sourceNode) {
      setCallTriggerPrompt(null)
      return
    }

    const connect = { getNodes, addNodes, addEdges, updateNodeInternals }

    // source -> Initiate Call
    const initiateCallNode = connectChildNode({
      sourceNode,
      outputHandle: prompt.sourceHandle,
      nodeType: FlowNodeTypes.initiate_call,
      position: prompt.position,
      ...connect,
    })
    if (!initiateCallNode) {
      setCallTriggerPrompt(null)
      return
    }

    // Initiate Call (answered) -> AI conversation
    connectChildNode({
      sourceNode: initiateCallNode,
      outputHandle: INITIATE_CALL_ANSWERED_HANDLE,
      nodeType: prompt.nodeType ?? FlowNodeTypes.ai_agent_v2,
      position: { x: prompt.position.x, y: prompt.position.y + 250 },
      ...connect,
    })

    if (prompt.isDrag) clearDragPlaceholder()
    setCallTriggerPrompt(null)
    // Open the Initiate Call config so the destination number is filled first.
    clickNode(initiateCallNode.id)
  }

  return (
    <Dialog open={!!callTriggerPrompt} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-h4-500">Active Call Required</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-p-400 text-secondary">
            An AI conversation on a call channel needs an active call. Add an{' '}
            <span className="text-p-600">Initiate Call</span> node before it so the call is placed
            first.
          </p>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="flex-1"
              data-testid="button-cancel-call-trigger"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddInitiateCall}
              className="flex-1"
              data-testid="button-add-initiate-call"
            >
              Add Initiate Call
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
